import React, { useContext, useEffect, useMemo, useState } from "react";
import { Container, Button, Table, Spinner } from "reactstrap";
import { queryClient } from "api/queryClient";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { IP_PATHS } from "Routes/paths";
import { useGlobalStudy } from "context/StudyContext";
import { useIpInventoryService } from "../api/useIpInventoryService";
import { showToastMessage } from "common/service/CommonService";
import FullPageLoader from "Components/Common/FullPageLoader";
import { getAcceptanceFormFields, getAcceptanceRecordFields } from "../helpers/ipInventory.constant";
import { AcceptanceFormValues, AcceptanceFieldItem } from "../helpers/ipInventory.types";
import { BreadcrumbContext } from "Layouts/Header";
import formStyles from "assets/scss/common/formStyle.module.scss";
import ipStyles from "../css/ipInventory.module.scss";
import CommonForm from "Components/Common/CommonForm";
import SectionCard from "Components/Common/SectionCard";
import TruncatedText from "Components/Common/TruncatedText";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";

// Props
export interface IpInventoryAcceptanceProps {
  ipid?: string | number | null;
  receiptnumber?: string | null;
  onBack?: () => void;
  isStepperMode?: boolean;
  data?: any;
}

const IpInventoryAcceptance: React.FC<IpInventoryAcceptanceProps> = ({
  ipid: propIpId,
  receiptnumber: propReceiptNumber,
  onBack,
  isStepperMode = false,
  data: propData,
}) => {
  /* ------------------------------------------------------------------------ */
  /* 1. ROUTING, CONTEXT & PARAMS                                             */
  /* ------------------------------------------------------------------------ */
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { selectedStudy: globalStudy } = useGlobalStudy();

  const activeIpId = propIpId ?? paramId;
  const activeReceiptNumber = propReceiptNumber ?? localStorage.getItem("receiptnumber");

  /* ------------------------------------------------------------------------ */
  /* 2. LOCAL STATE & HELPERS                                                 */
  /* ------------------------------------------------------------------------ */
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<AcceptanceFormValues | null>(null);
  const [qtyErrorDetails, setQtyErrorDetails] = useState<{
    totalQty: number;
    maxQty: number;
    trainQty: number;
    physQty: number;
  } | null>(null);

  const getTodayString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  /* ------------------------------------------------------------------------ */
  /* 3. REACT HOOK FORM SETUP                                                 */
  /* ------------------------------------------------------------------------ */
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<AcceptanceFormValues>({
    defaultValues: {
      acceptancedate: getTodayString(),
      selectedcontainer: "",
      primarycontainer: "no",
      trainingqty: null,
      physicalverificationqty: null,
      assaydifference: null,
      ipdescription: null,
      comments: null,
    },
    mode: "onChange",
  });

  /* ------------------------------------------------------------------------ */
  /* 4. FORM WATCHERS                                                         */
  /* ------------------------------------------------------------------------ */
  const selectedAcceptanceDate = watch("acceptancedate");
  const selectedContainerVal = watch("selectedcontainer");
  const primaryContainerVal = watch("primarycontainer");
  const trainingQtyVal = watch("trainingqty");
  const physicalVerificationQtyVal = watch("physicalverificationqty");

  /* ------------------------------------------------------------------------ */
  /* 5. API & SERVICES QUERIES                                                */
  /* ------------------------------------------------------------------------ */
  const { SelectIdQuery, InsertAcceptanceMutation, UpdateAcceptanceMutation } = useIpInventoryService({
    ipid: activeIpId,
    studyid: globalStudy?.studyid,
    receiptnumber: activeReceiptNumber,
  });

  const data = propData ?? SelectIdQuery.data?.data?.[0];

  /* ------------------------------------------------------------------------ */
  /* 6. (useMemo)                                   */
  /* ------------------------------------------------------------------------ */
  const existingAcceptance = useMemo(() => {
    return Array.isArray(data?.acceptancedetails)
      ? data.acceptancedetails[0]
      : data?.acceptancedetails;
  }, [data?.acceptancedetails]);

  const containerNumberOptions = useMemo(() => {
    if (Array.isArray(data?.containers)) {
      return data.containers
        .filter((c: any) => c.isusable !== false)
        .map((c: any) => {
          const num = c.containernumber;
          return {
            label: num,
            value: num,
          };
        });
    }
    return [];
  }, [data?.containers]);

  const acceptanceFields = useMemo(() => getAcceptanceRecordFields(data), [data]);

  const pairedAcceptanceFields: AcceptanceFieldItem[][] = useMemo(() => {
    if (!acceptanceFields.length) return [];
    const paired: AcceptanceFieldItem[][] = [];
    for (let i = 0; i < acceptanceFields.length; i += 2) {
      paired.push(acceptanceFields.slice(i, i + 2));
    }
    return paired;
  }, [acceptanceFields]);

  const formFields = getAcceptanceFormFields(containerNumberOptions, primaryContainerVal);

  /* ------------------------------------------------------------------------ */
  /* 7. (useEffect)                                              */
  /* ------------------------------------------------------------------------ */

  // Clear fields when primarycontainer is not "yes"
  useEffect(() => {
    if (primaryContainerVal && primaryContainerVal !== "yes") {
      setValue("trainingqty", null);
      setValue("physicalverificationqty", null);
      setValue("assaydifference", null);
      setValue("ipdescription", null);
    }
  }, [primaryContainerVal, setValue]);

  // Breadcrumb navigation setup
  useEffect(() => {
    if (!isStepperMode) {
      breadcrumbContext?.setBreadcrumbItems?.([
        { text: "Investigational Product", link: IP_PATHS.LIST },
        { text: "IP Acceptance Sheet" },
      ]);

      return () => breadcrumbContext?.setBreadcrumbItems?.([]);
    }
  }, [isStepperMode]);

  // Pre-fill acceptance values on edit
  useEffect(() => {
    if (existingAcceptance) {
      if (existingAcceptance.acceptancedate) {
        setValue("acceptancedate", existingAcceptance.acceptancedate.split("T")[0]);
      }
      if (existingAcceptance.selectedcontainer) {
        setValue("selectedcontainer", String(existingAcceptance.selectedcontainer));
      }
      if (existingAcceptance.trainingqty != null) {
        setValue("trainingqty", existingAcceptance.trainingqty);
      }
      if (existingAcceptance.physicalverificationqty != null) {
        setValue("physicalverificationqty", existingAcceptance.physicalverificationqty);
      }
      const details = existingAcceptance.acceptancedetails || existingAcceptance;
      if (details) {
        if (details.comments != null) setValue("comments", details.comments);
        if (details.ipdescription != null) setValue("ipdescription", details.ipdescription);
        if (details.assaydifference != null) setValue("assaydifference", details.assaydifference);
        if (details.primarycontainer != null) setValue("primarycontainer", details.primarycontainer);
      }
    }
  }, [existingAcceptance, setValue]);

  // Quantity Validation Warning Banner watcher
  useEffect(() => {
    if (!selectedContainerVal) {
      setQtyErrorDetails(null);
      return;
    }

    const selectedCont = data?.containers?.find(
      (c: any) => String(c.containernumber) === String(selectedContainerVal)
    );
    if (!selectedCont) {
      setQtyErrorDetails(null);
      return;
    }

    const maxQty = Number(selectedCont?.initialquantity || 0);
    const trainQty = Number(trainingQtyVal || 0);
    const physQty = Number(physicalVerificationQtyVal || 0);
    const totalQty = trainQty + physQty;

    const isTyped = (trainingQtyVal !== "" && trainingQtyVal !== null && trainingQtyVal !== undefined) ||
                    (physicalVerificationQtyVal !== "" && physicalVerificationQtyVal !== null && physicalVerificationQtyVal !== undefined);

    if (isTyped && totalQty > maxQty) {
      setQtyErrorDetails({ totalQty, maxQty, trainQty, physQty });
    } else {
      setQtyErrorDetails(null);
    }
  }, [selectedContainerVal, trainingQtyVal, physicalVerificationQtyVal, data?.containers]);

  /* ------------------------------------------------------------------------ */
  /* 8. EVENT HANDLERS & FORM SUBMISSION                                      */
  /* ------------------------------------------------------------------------ */
  const handleSubmitForm = (formData: AcceptanceFormValues) => {
    if (qtyErrorDetails) return;

    if (existingAcceptance) {
      setPendingFormData(formData);
      setIsUpdateModalOpen(true);
    } else {
      executeInsert(formData);
    }
  };

  const executeInsert = (formData: AcceptanceFormValues) => {
    const payload = {
      ipid: data?.ipid || activeIpId,
      studyid: data?.studyid || globalStudy?.studyid,
      receiptnumber: data?.receiptnumber || activeReceiptNumber,
      acceptancedate: formData.acceptancedate,
      selectedcontainer: formData.selectedcontainer,
      trainingqty: formData.trainingqty,
      physicalverificationqty: formData.physicalverificationqty,
      comments: formData.comments,
      ipdescription: formData.ipdescription,
      assaydifference: formData.assaydifference,
      primarycontainer: formData.primarycontainer,
    };

    InsertAcceptanceMutation.mutate(payload, {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries({ queryKey: ["ipinventory-selectAll"] });
        showToastMessage("success", res?.message || "IP Acceptance details submitted successfully!");
        navigate(IP_PATHS.LIST);
      },
      onError: (err: any) => {
        showToastMessage("error", err);
      },
    });
  };

  const handleUpdate = (reasonPayload: ReasonSubmitPayload) => {
    if (!pendingFormData) return;

    const payload = {
      acceptanceid: existingAcceptance?.acceptanceid || null,
      ipid: data?.ipid || activeIpId,
      studyid: data?.studyid || globalStudy?.studyid,
      receiptnumber: data?.receiptnumber || activeReceiptNumber,
      acceptancedate: pendingFormData.acceptancedate,
      selectedcontainer: pendingFormData.selectedcontainer,
      trainingqty: pendingFormData.trainingqty,
      physicalverificationqty: pendingFormData.physicalverificationqty,
      comments: pendingFormData.comments,
      ipdescription: pendingFormData.ipdescription,
      assaydifference: pendingFormData.assaydifference,
      primarycontainer: pendingFormData.primarycontainer,
      updatereason: reasonPayload.reason,
    };

    UpdateAcceptanceMutation.mutate(payload, {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries({ queryKey: ["ipinventory-selectAll"] });
        showToastMessage("success", res?.message || "IP Acceptance details updated successfully!");
        setIsUpdateModalOpen(false);
        navigate(IP_PATHS.LIST);
      },
      onError: (err: any) => {
        showToastMessage("error", err);
      },
    });
  };

  /* ------------------------------------------------------------------------ */
  /* 9. RENDER COMPONENT                                                      */
  /* ------------------------------------------------------------------------ */
  if (!propData && (SelectIdQuery.isLoading || SelectIdQuery.isFetching)) {
    return (
      <Container fluid className={`px-0 ${formStyles.pageContainer}`}>
        <FullPageLoader />
      </Container>
    );
  }

  if (!data) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
            <h4 className="fw-600">Record Not Found</h4>
            <p className="text-muted">
              The IP Inventory acceptance record you are looking for does not exist.
            </p>
            <Button color="primary" onClick={() => navigate(IP_PATHS.LIST)}>
              Back to IP List
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className={isStepperMode ? "" : "page-content"}>
      <Container fluid className={`px-0 ${isStepperMode ? "" : formStyles.pageContainer}`}>
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <div className={isStepperMode ? "px-0" : "px-3 py-2"}>
            {/* Header Action Bar */}
            {!isStepperMode && (
              <div className="d-flex justify-content-between align-items-center mb-3 d-print-none">
                <div>
                  <h4 className="fw-bold mb-1 text-dark">IP Inventory Acceptance Sheet</h4>
                  <p className="text-muted mb-0 fs-13">
                    Receipt No: <strong className="text-dark">{data.receiptnumber || "—"}</strong> | Study Code: <strong className="text-dark">{data.studycode || "—"}</strong>
                  </p>
                </div>
              </div>
            )}

            {/* 1. IP Record Details */}
            <SectionCard
              index={1}
              icon="ri-file-paper-2-line"
              title="Investigational Product Acceptance Record"
              subtitle="Documented upon receipt and verification at Pharmacy Department"
            >
              <div className="table-responsive">
                <Table bordered size="sm" className="align-middle mb-0" style={{ fontSize: "13px" }}>
                  <tbody>
                    {pairedAcceptanceFields.map((row, idx) => (
                      <tr key={idx}>
                        <td className="fw-semibold text-dark align-middle px-3 py-2 bg-light border-end" style={{ width: "18%" }}>
                          {row[0].label}
                        </td>
                        <td className="align-middle px-3 py-2 text-dark border-end" style={{ width: "32%" }}>
                          <TruncatedText text={row[0].value} maxWidth="150px" />
                        </td>
                        {row[1] ? (
                          <>
                            <td className="fw-semibold text-dark align-middle px-3 py-2 bg-light border-end" style={{ width: "18%" }}>
                              {row[1].label}
                            </td>
                            <td className="align-middle px-3 py-2 text-dark" style={{ width: "32%" }}>
                              <TruncatedText text={row[1].value} maxWidth="150px" />
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="bg-light border-end" style={{ width: "18%" }}></td>
                            <td style={{ width: "32%" }}></td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </SectionCard>

            {/* 2. Container Details */}
            <SectionCard
              index={2}
              icon="ri-archive-line"
              title="Container Details"
              subtitle="Container breakdown, quantities, container types, and physical condition."
            >
              <div className="table-responsive">
                <Table bordered size="sm" className="align-middle text-center mb-0" style={{ fontSize: "13px" }}>
                  <thead className="bg-light text-dark">
                    <tr>
                      <th style={{ width: "120px" }} className="py-2 fw-semibold">Container No.</th>
                      <th style={{ width: "140px" }} className="py-2 fw-semibold">Quantity</th>
                      <th style={{ width: "200px" }} className="py-2 fw-semibold">Container Type</th>
                      <th style={{ width: "150px" }} className="py-2 fw-semibold">Condition</th>
                      <th style={{ width: "100px" }} className="py-2 fw-semibold">Not For Use</th>
                      <th className="text-start py-2 fw-semibold">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(data.containers) && data.containers.length > 0 ? (
                      data.containers.map((c: any, idx: number) => (
                        <tr key={idx}>
                          <td className="fw-medium text-dark">
                            {c.containernumber || String(idx + 1).padStart(2, "0")}
                          </td>
                          <td className="fw-semibold text-dark">
                            {c.initialquantity != null ? c.initialquantity : "—"}
                          </td>
                          <td className="text-dark">{c.containertypetext || "—"}</td>
                          <td>
                            {c.containerconditiontext || c.containercondition ? (
                              <span className="text-capitalize fw-medium text-dark">
                                {String(c.containerconditiontext || c.containercondition)}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input my-0"
                              checked={c.isusable === false}
                              disabled
                              readOnly
                            />
                          </td>
                          <td className="text-start text-dark"><TruncatedText text={c.containerremarks || "—"} maxWidth="350px" /></td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-muted fst-italic py-3">
                          No container details available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </SectionCard>

            {/* 3. Acceptance Verification Form */}
            <SectionCard
              index={3}
              icon="ri-checkbox-circle-line"
              title="IP Description & Acceptance Verification"
              subtitle="Record acceptance details, container verification, and observations."
            >
              {qtyErrorDetails && (
                <div className={ipStyles.qtyAlertBanner}>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <i className="ri-error-warning-fill text-danger fs-16" />
                    <span className="fs-12 fw-medium text-dark">
                      Total removed quantity (<strong className="text-danger">{qtyErrorDetails.totalQty}</strong>) exceeds selected container limit (<strong className="text-dark">{qtyErrorDetails.maxQty}</strong>).
                    </span>
                  </div>
                  <span className="fs-11 text-muted ms-auto fst-italic">
                    Training: <strong>{qtyErrorDetails.trainQty}</strong> | Physical Verification: <strong>{qtyErrorDetails.physQty}</strong>
                  </span>
                </div>
              )}
              <CommonForm
                fields={formFields}
                control={control}
                errors={errors}
                columns={2}
                setValue={setValue}
                getValues={getValues}
              />
            </SectionCard>
          </div>

          {/* Full Page Sticky Footer */}
          <div className={formStyles.stickyFooter}>
            <div className="d-flex justify-content-between align-items-center w-100">
              {isStepperMode && onBack ? (
                <Button
                  type="button"
                  color="dark"
                  outline
                  onClick={onBack}
                >
                  Back
                </Button>
              ) : <div />}
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  color="primary"
                  outline
                  disabled={InsertAcceptanceMutation.isPending || UpdateAcceptanceMutation.isPending}
                  onClick={handleSubmit(handleSubmitForm)}
                >
                  {InsertAcceptanceMutation.isPending && (
                    <Spinner size="sm" className="me-1" />
                  )}
                  {existingAcceptance ? "Update" : "Save"}
                </Button>
              </div>
            </div>
          </div>

        </form>

        {/* Reason Modal */}
        <Reason
          isOpen={isUpdateModalOpen}
          toggle={() => setIsUpdateModalOpen(false)}
          action="Update Acceptance Record"
          id={Number(existingAcceptance?.acceptanceid || data?.ipid || 0)}
          click={handleUpdate}
          isLoading={UpdateAcceptanceMutation.isPending}
        />
      </Container>
    </div>
  );
};

export default IpInventoryAcceptance;
